'use client'

import { useEffect } from 'react'
import { PhysicalPadUI } from '@/stores/usePadsStore'
import { PadManager } from '@/services/VirtualPad/PadManager'

/**
 * Converts a browser Gamepad into the serializable model
 * used by the UI store.
 */
function mapPhysicalGamepad(gamepad: Gamepad): PhysicalPadUI {
  return {
    index: gamepad.index,
    id: gamepad.id
  }
}

/**
 * Returns all currently connected physical gamepads.
 */
function getConnectedPhysicalGamepads(): PhysicalPadUI[] {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.getGamepads !== 'function'
  ) {
    return []
  }

  return Array.from(navigator.getGamepads())
    .filter(
      (gamepad): gamepad is Gamepad =>
        gamepad !== null &&
        gamepad.connected
    )
    .map(mapPhysicalGamepad)
    .sort((a, b) => a.index - b.index)
}

/**
 * Reads the currently connected browser gamepads and delegates their synchronization to PadManager
 */
export function useSyncPhysicalGamepads(): void {
  useEffect(() => {
    const syncPhysicalGamepads = (): void => {
      const physicalGamepads = getConnectedPhysicalGamepads()
      // console.log('PHYSICAL GAMEPADS :: ')
      // console.log(physicalGamepads)
      PadManager.getInstance().setPhysicalPads(physicalGamepads)
    }

    const handleGamepadConnected = (event: GamepadEvent): void => {
      // console.info(`GAMEPAD :: CONNECTED :: ${event.gamepad.id}`)
      syncPhysicalGamepads()
    }

    const handleGamepadDisconnected = (event: GamepadEvent): void => {
      // console.info(`GAMEPAD :: DISCONNECTED :: ${event.gamepad.id}`)
      syncPhysicalGamepads()
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') syncPhysicalGamepads()
    }

    window.addEventListener('gamepadconnected', handleGamepadConnected)
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected)
    window.addEventListener('focus', syncPhysicalGamepads)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    syncPhysicalGamepads()

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected)
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected)
      window.removeEventListener('focus', syncPhysicalGamepads)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])
}